

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import Datim from "@/components/shared/Datim.vue";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import {BoListPanel} from "bonomo/dist";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {Prop} from "vue-property-decorator";
import LockerSearch from "@/components/locker/widget/LockerSearch.vue";
import NewStandaloneDialog from "@/views/standalone/NewStandaloneDialog.vue";
import StandaloneEditDialog from "@/views/standalone/StandaloneEditDialog.vue";
import { IListStandalone } from '@/model/Standalone/standaloneInterfaces';
import {IOnOffHandlers} from "bonomo/dist";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import {UrlHelper} from "@/helpers/UrlHelper";
import {EPermissionActions} from "@/model/EPermissionActions";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";
import {IAuthUserData} from "@/store/modules/authModule";
import LMScreen from "@/components/locker/modules/LMScreen.vue";
import StandaloneScreenshotPreview from "@/views/standalone/StandaloneScreenshotPreview.vue";
import CommunityNav from "@/components/community/widget/CommunityNav.vue";
import CreateButton from "@/components/shared/CreateButton.vue";
import RowActions from "@/components/shared/RowActions.vue";
import {IRowAction} from "@/model/IRowAction";
import { ICommunity } from "@/model/Community/communityInterfaces";

const lockerModule = namespace("lockerModule");
const standaloneModule = namespace("standaloneModule");
const authModule = namespace("authModule");
const communityModule = namespace("communityModule");

@Component({
  components: {
    LMScreen,
    BoListPanel,
    LockerChip,
    Datim,
    StandaloneScreenshotPreview,
    LockerSearch,
    NewStandaloneDialog,
    StandaloneEditDialog,
    SimpleConfirmationDialog,
    CommunityNav,
    CreateButton,
    RowActions,
  }
})

export default class StandalonesList extends Vue {
  @Prop({default: 0})
  private lockerId!: number;
  @Prop({default: 0})
  private communityIdProp!: number;
  private standaloneId!: number;
  private showCreateDialog: boolean = false;
  private showEditDialog: boolean = false;
  private editingStandalone: IListStandalone | null = null;

  @standaloneModule.Getter('standaloneList')
  private standalones!: IListStandalone[];

  @lockerModule.Getter('lockers')
  private lockers!: ILocker[];

  @authModule.Getter('userData')
  authUser!: IAuthUserData;

  @communityModule.Getter('community')
  private communityData!: ICommunity | null;

  @communityModule.Action('fetchCommunity')
  private fetchCommunityAction!: (communityId: number) => Promise<any>;

  @standaloneModule.Action('fetchStandalones')
  protected fetchStandalonesAction!: ({lockerId, filters}: any) => Promise<any>;

  @standaloneModule.Action('fetchStandalonesByCommunity')
  protected fetchStandalonesByCommunityAction!: ({communityId, filters}: any) => Promise<any>;

  submitting: boolean = false;
  showRebootConfirmation: boolean = false;
  showRefreshConfirmation: boolean = false;
  showSshConfirmation: boolean = false;
  showMarkDemoConfirmation: boolean = false;

  private activeStandaloneId: number | null = null;
  private activeLockerId: number | null = null;
  private standaloneDeviceId: string | null = null;
  private markDemoStandaloneId: number | null = null;

  ons: IOnOffHandlers = {};

   public created() {
      this.ons = this.$ons({
        'backend.call.OK.rebootLocker': this.onRebootLockerOK,
        'backend.call.KO.rebootLocker': this.onRebootLockerKO,
        'backend.call.OK.sshConnection': this.onSshConnectionOK,
        'backend.call.KO.sshConnection': this.onSshConnectionKO,
        'backend.call.OK.refreshBrowser': this.onRefreshBroserOK,
        'backend.call.KO.refreshBrowser': this.onRefreshBroserKO,
        'backend.call.OK.standaloneDemoMarked': this.onMarkDemoOK,
        'backend.call.KO.standaloneDemoMarked': this.onMarkDemoKO,
      });
      if (this.communityIdProp > 0 && (!this.communityData || this.communityData.id !== this.communityIdProp)) {
        this.fetchCommunityAction(this.communityIdProp);
      }
    }


  protected get isLockerContext(): boolean {
    return this.lockerId > 0;
  }

  protected get isCommunityContext(): boolean {
    return !this.isLockerContext && this.communityIdProp > 0;
  }

  protected get pageTitle(): string {
    if (this.isLockerContext) {
      return this.$t('lockers.standalone.title') + ' ' + this.getLockerName(this.lockerId);
    }
    if (this.isCommunityContext) {
      const communityName = this.communityData && this.communityData.name ? ' ' + this.communityData.name : '';
      return (this.$t('lockers.standalone.title') as string) + communityName;
    }
    return '...';
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'standalone_id' },
      { text: this.$t('standalone.detail.name.title'), align: 'start', sortable: false, value: 'name' },
      { text: this.$t('standalone.list.installCode'), align: 'end', sortable: false, value: 'install_code' },
      { text: this.$t('standalone.list.deviceId'), align: 'start', sortable: false, value: 'device_id' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: false, value: 'locker_id' },
      { text: this.$t('standalone.list.lastPing'), align: 'start', sortable: false, value: 'last_ping' },
      { text: this.$t('standalone.list.statuses.title'), align: 'start', sortable: false, value: 'health.status' },
      { text: this.$t('standalone.list.metrics'), align: 'start', sortable: false, value: 'metrics' },
      { text: this.$t('standalone.list.logs_location.title'), align: 'start', sortable: false, value: 'health.logs_location' },
      { text: this.$t('standalone.detail.sim.title'), align: 'start', sortable: false, value: 'sim' },
      { text: this.$t('standalone.list.deviceType'), align: 'start', sortable: false, value: 'digital_portal_type' },
      { text: this.$t('standalone.list.actions.title'), align: 'end', value: 'detailButton', width: '1%'},
    ];
  }

  protected loading: boolean = false;

  protected get communityId(): number {
    if (this.communityIdProp > 0) return this.communityIdProp;
    const locker = this.lockers.find(l => l.id == this.lockerId);
    return locker ? locker.community_id : 0;
  }

  getLockerName(lockerId: number): string {
    for (let i = 0; i < this.lockers.length; i ++) {
      if(this.lockers[i].id == lockerId) {
        return this.lockers[i].name;
      }
    }
    return '...';
  }

  // Most used first: preview and edit stay visible, the rest go to the row's "more" menu
  rowActions(item: IListStandalone): IRowAction[] {
    const canOperate = this.isOperator || this.isDeveloper;
    const actions: IRowAction[] = [];
    if (item.device_id) {
      actions.push({ key: 'preview', icon: 'mdi-cellphone', title: this.$t('standalone.list.actions.titles.preview') as string, handler: () => this.getPreviewUrl(String(item.device_id)) });
    }
    actions.push({ key: 'edit', icon: 'mdi-pencil', title: this.$t('standalone.detail.title') as string, handler: () => this.onEditClicked(item) });
    if (item.device_id && canOperate) {
      actions.push({ key: 'refresh', icon: 'mdi-web-refresh', title: this.$t('standalone.list.actions.titles.refresh') as string, disabled: this.submitting, handler: () => this.onRefreshClicked(item.standalone_id) });
      actions.push({ key: 'screenshot', icon: 'mdi-camera', title: this.$t('standalone.list.actions.titles.screenshot') as string, disabled: this.submitting, handler: () => this.onScreenshotClicked(item.standalone_id, item.locker_id, String(item.device_id)) });
    }
    if (item.device_id && this.isDeveloper) {
      actions.push({ key: 'reboot', icon: 'mdi-restart', title: this.$t('standalone.list.actions.titles.reboot') as string, disabled: this.submitting, handler: () => this.onRebootClicked(item.standalone_id) });
      actions.push({ key: 'ssh', icon: 'mdi-ssh', title: this.$t('standalone.list.actions.titles.ssh') as string, disabled: this.submitting, handler: () => this.onSshConnectionClicked(item.standalone_id) });
    }
    return actions;
  }

  getPreviewUrl(deviceId: string): void {
    const route = this.$router.resolve({
      name: 'standalonePreview', 
      params: {deviceId, lockerId: String(this.lockerId)}
    });
    window.open(route.href, '_blank', 'noopener');
  }

  protected get isDeveloper() {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER);
  }

  protected get isOperator() {
    const authUser = this.authUser;
    return PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_LOCKER_OPERATOR, this.communityId);
  }


  private onCreateButton() {
    this.showCreateDialog = true;
  }

  private onLockerSelected(newLockerId: number) {
    this.$router.push({name: 'standalonesList', params: {lockerId: '' + newLockerId}});
  }

  private fetchStandalones(filters: any) {
    if (this.isCommunityContext) {
      this.fetchStandalonesByCommunityAction({communityId: this.communityIdProp, filters});
    } else {
      this.fetchStandalonesAction({lockerId: this.lockerId, filters});
    }
  }

  protected onRebootClicked(standaloneId: number) {
    this.standaloneId = standaloneId;
    this.showRebootConfirmation = true;
  }

  protected onRefreshClicked(standaloneId: number) {
    this.standaloneId = standaloneId;
    this.showRefreshConfirmation = true;
  }

  protected onRefreshBrowser() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/refreshBrowser', {
      standaloneId: this.standaloneId
    });
  }
  
  protected onRebootLocker() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/rebootStandalone', {
      standaloneId: this.standaloneId
    });
  }
  private onRebootLockerOK() {
    this.submitting = false;
    this.showRebootConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }
  private onRebootLockerKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  private onRefreshBroserOK() {
    this.submitting = false;
    this.showRefreshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onRefreshBroserKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onSshConnectionClicked(standaloneId: number) {
    this.standaloneId = standaloneId;
    this.showSshConfirmation = true;
  }

  protected onSshConnection() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/sshConnection', {
      standaloneId: this.standaloneId
    });
  }

  private onSshConnectionOK() {
    this.submitting = false;
    this.showSshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onSshConnectionKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onMarkDemoClicked(standalone: IListStandalone) {
    this.markDemoStandaloneId = standalone.standalone_id;
    this.showMarkDemoConfirmation = true;
  }

  protected onMarkDemo() {
    if (!this.markDemoStandaloneId) {
      return;
    }
    this.submitting = true;
    this.$store.dispatch('standaloneModule/markStandaloneAsDemo', {standaloneId: this.markDemoStandaloneId});
  }

  private onMarkDemoOK() {
    this.submitting = false;
    this.showMarkDemoConfirmation = false;
    this.markDemoStandaloneId = null;
    this.$uiBus.emit('success', 'OK');
  }

  private onMarkDemoKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onScreenshotClicked(standaloneId: number, lockerId: number, standaloneDeviceId: string) {
    this.activeStandaloneId = standaloneId;
    this.activeLockerId = lockerId;
    this.standaloneDeviceId = standaloneDeviceId;
  }

  protected onEditClicked(standalone: IListStandalone) {
    this.editingStandalone = standalone;
    this.showEditDialog = true;
  }

  protected onStandaloneDeleted() {
    this.showEditDialog = false;
    this.editingStandalone = null;
  }

  protected onCloseScreenshotPreview() {
    this.activeStandaloneId = null;
    this.activeLockerId = null;
    this.standaloneDeviceId = null;
  }

}
